import {
  type IGetActionsData,
  type TGetCommunicationTemplatesFilled,
  useGetCommunicationTemplatesFilledSuspense,
} from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { IChallenge } from "@bbj-types/challenge";
import { CommunicationTemplateType, FeatureTemplate } from "@bbj-types/communication/enum";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApplyTransition } from "@bbj-types/socialApply";
import type { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import { Dialog, type Theme, useMediaQuery } from "@mui/material";
import { SuspenseWrapper } from "@styleguide";
import { Actions } from "./actions/actions";
import { SendSocialOfferModalContent } from "./body/modalContent";
import { Header } from "./header";
import { useSendSocialOfferModal } from "./logics";
import { SendSocialOfferProvider } from "./logics/context";

export interface ISendSocialOfferModalProps {
  open: boolean;
  onClose: () => void;
  offer: ISocialOffer & {
    status?: SocialApplyStatus;
    transitions?: ISocialApplyTransition[];
    socialApplyId?: string;
  };
  candidate: ICandidate;
  challenges: IChallenge[];
  onSuccess: (
    socialApply: { _id: string; status: string; transitions: ISocialApplyTransition[] },
    action: IGetActionsData
  ) => void;
}

interface IModalContentProps {
  getTemplates: () => TGetCommunicationTemplatesFilled;
  offer: ISocialOffer & {
    status?: SocialApplyStatus;
    transitions?: ISocialApplyTransition[];
    socialApplyId?: string;
  };
  candidate: ICandidate;
  challenges: IChallenge[];
  onSuccess: (
    socialApply: { _id: string; status: string; transitions: ISocialApplyTransition[] },
    action: IGetActionsData
  ) => void;
  onClose: () => void;
}

const ModalContent = ({ getTemplates, offer, candidate, challenges, onSuccess, onClose }: IModalContentProps) => {
  const { handleClose, ...hookValues } = useSendSocialOfferModal(
    offer,
    candidate,
    challenges,
    onSuccess,
    onClose,
    getTemplates
  );

  const contextValues = {
    offer,
    candidate,
    challenges,
    onSuccess,
    handleClose,
    ...hookValues,
  };

  return (
    <SendSocialOfferProvider values={contextValues}>
      <>
        <Header />
        <SendSocialOfferModalContent />
        <Actions />
      </>
    </SendSocialOfferProvider>
  );
};

export const SendSocialOfferModal = ({
  open,
  onClose,
  offer,
  candidate,
  challenges,
  onSuccess,
}: ISendSocialOfferModalProps) => {
  const md = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={!md}
      slotProps={{
        paper: {
          sx: {
            paddingRight: 1,
            paddingLeft: 1,
            paddingBottom: 2.5,
            ...(md ? { minHeight: "550px", minWidth: "900px" } : undefined),
          },
        },
      }}
    >
      <SuspenseWrapper
        useSuspenseHook={useGetCommunicationTemplatesFilledSuspense}
        suspenseHookArgs={[
          {
            feature: FeatureTemplate.SOCIAL_OFFER,
            candidateId: candidate._id,
            offerId: offer.offerId,
            type: [CommunicationTemplateType.EMAIL, CommunicationTemplateType.SMS],
          },
        ]}
        targetProps="getTemplates"
        TargetComponent={ModalContent}
        targetComponentProps={{
          offer,
          candidate,
          challenges,
          onSuccess,
          onClose,
        }}
        slotProps={{
          alert: { sx: { display: "none" } },
          box: { sx: { display: "none" } },
        }}
      />
    </Dialog>
  );
};
